import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ArrowUpTrayIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { investigationsService } from '@/services/investigations.service';
import toast from 'react-hot-toast';
import {
  DOCUMENT_ACCEPT_STRING,
  FILE_SIZE_LIMITS,
  FILE_UPLOAD_ERRORS,
  FILE_TYPE_LABELS,
  isValidDocumentMimeType,
} from '@/constants/file-upload';

interface UploadAttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  investigationId: string;
  onUploadComplete: () => void;
}

type EvidenceType = 'DOCUMENT' | 'PHOTO' | 'VIDEO' | 'AUDIO' | 'OTHER';

// Use centralized file type configuration
const MAX_FILE_SIZE_MB = FILE_SIZE_LIMITS.DOCUMENT_MAX_SIZE_MB;
const MAX_FILE_SIZE_BYTES = FILE_SIZE_LIMITS.DOCUMENT_MAX_SIZE_BYTES;

// Utility function to detect evidence type from MIME type
function getEvidenceTypeFromMimeType(mimeType: string): EvidenceType {
  if (mimeType.startsWith('image/')) {
    return 'PHOTO';
  }
  if (mimeType.startsWith('video/')) {
    return 'VIDEO';
  }
  if (mimeType.startsWith('audio/')) {
    return 'AUDIO';
  }
  if (
    mimeType === 'application/pdf' ||
    mimeType === 'application/msword' ||
    mimeType.startsWith('application/vnd.openxmlformats') ||
    mimeType.startsWith('application/vnd.ms-') ||
    mimeType === 'text/plain'
  ) {
    return 'DOCUMENT';
  }
  return 'OTHER';
}

// Get icon for evidence type
function getEvidenceIcon(type: EvidenceType) {
  switch (type) {
    case 'PHOTO':
      return PhotoIcon;
    case 'VIDEO':
      return VideoCameraIcon;
    case 'AUDIO':
      return MusicalNoteIcon;
    case 'DOCUMENT':
    case 'OTHER':
    default:
      return DocumentIcon;
  }
}

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadAttachmentModal({
  isOpen,
  onClose,
  investigationId,
  onUploadComplete,
}: UploadAttachmentModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file
  const validateFile = (file: File): string | null => {
    // Check file type
    if (!isValidDocumentMimeType(file.type)) {
      return FILE_UPLOAD_ERRORS.INVALID_DOCUMENT_TYPE;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `${FILE_UPLOAD_ERRORS.FILE_TOO_LARGE_DOCUMENT} Current size: ${formatFileSize(file.size)}`;
    }

    return null;
  };

  // Handle file selection
  const handleFileChange = useCallback((file: File) => {
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
  }, []);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  // Handle click on drop zone
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Handle remove file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // Auto-detect evidence type from MIME type
      const evidenceType = getEvidenceTypeFromMimeType(selectedFile.type);

      // Upload file
      await investigationsService.uploadAttachment(
        investigationId,
        selectedFile,
        description ?? undefined,
        evidenceType
      );

      // Show success message
      toast.success('Attachment uploaded successfully');

      // Call completion callback
      onUploadComplete();

      // Reset and close
      handleClose();
    } catch (err) {
      console.error('Failed to upload attachment:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to upload attachment';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (isUploading) {
      return; // Don't allow closing while uploading
    }
    setSelectedFile(null);
    setDescription('');
    setError(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Get evidence type for selected file
  const evidenceType = selectedFile
    ? getEvidenceTypeFromMimeType(selectedFile.type)
    : null;
  const EvidenceIcon = evidenceType ? getEvidenceIcon(evidenceType) : DocumentIcon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-lg w-full pointer-events-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center space-x-3">
                  <ArrowUpTrayIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                    Upload Attachment
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isUploading}
                  className="p-2 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* File upload zone */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={DOCUMENT_ACCEPT_STRING}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={isUploading}
                  />

                  {!selectedFile ? (
                    <div
                      onClick={handleClick}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`relative cursor-pointer rounded-lg border-2 border-dashed p-8 transition-all ${
                        isDragging
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : error
                          ? 'border-danger-300 bg-danger-50 dark:border-danger-600 dark:bg-danger-900/20'
                          : 'border-secondary-300 hover:border-primary-400 dark:border-secondary-600 dark:hover:border-primary-500 hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
                      }`}
                    >
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div
                          className={`w-16 h-16 rounded-full flex items-center justify-center ${
                            isDragging
                              ? 'bg-primary-100 dark:bg-primary-900/40'
                              : error
                              ? 'bg-danger-100 dark:bg-danger-900/40'
                              : 'bg-secondary-100 dark:bg-secondary-800'
                          }`}
                        >
                          <ArrowUpTrayIcon
                            className={`w-8 h-8 ${
                              isDragging
                                ? 'text-primary-600 dark:text-primary-400'
                                : error
                                ? 'text-danger-600 dark:text-danger-400'
                                : 'text-secondary-400 dark:text-secondary-500'
                            }`}
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">
                            {isDragging
                              ? 'Drop file here'
                              : 'Click to upload or drag and drop'}
                          </p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                            {FILE_TYPE_LABELS.ALL} up to {MAX_FILE_SIZE_MB}MB
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative rounded-lg border-2 border-secondary-200 dark:border-secondary-700 p-4 bg-secondary-50 dark:bg-secondary-800/50">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <EvidenceIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                            {selectedFile.name}
                          </p>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              {formatFileSize(selectedFile.size)}
                            </span>
                            <span className="text-xs text-secondary-400">•</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                              {evidenceType}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          disabled={isUploading}
                          className="flex-shrink-0 p-1 text-secondary-400 hover:text-danger-600 dark:hover:text-danger-400 rounded transition-colors disabled:opacity-50"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Description{' '}
                    <span className="text-secondary-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isUploading}
                    placeholder="Enter a description for this attachment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    Provide context about this evidence (e.g., &quot;Access logs from server&quot;, &quot;Photo of
                    crime scene&quot;)
                  </p>
                </div>

                {/* Error message */}
                {error && (
                  <div className="flex items-start space-x-2 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                    <ExclamationCircleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
                  </div>
                )}

                {/* Success indicator (shown during upload) */}
                {isUploading && (
                  <div className="flex items-center space-x-2 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent" />
                    <p className="text-sm text-primary-700 dark:text-primary-300">
                      Uploading attachment...
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading}
                  loading={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload Attachment'}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
